import { useLingui } from "@lingui/react/macro";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { foldText } from "@/utils/text";
import { matchesTokens, searchTokens } from "@/utils/text-search";

import type { SearchResultDetail } from "./ui-kit/search-palette";

import { CommandGroup } from "./ui-kit/base/command";
import {
  MAX_SEARCH_RESULTS,
  SearchPalette,
  SearchPaletteItem,
} from "./ui-kit/search-palette";

/** A patient as the search palettes render one. */
export interface PatientResult {
  id: number;
  name?: string | null;
  /** Detail under the name, joined with "·". Falsy parts are dropped. */
  subtitle?: SearchResultDetail[];
}

/**
 * ⌘K palette over the patients in one table: a header trigger plus the dialog
 * it opens, matching on patient id and name. Selecting a result opens that
 * patient's profile.
 *
 * Only one palette may be mounted per page — the ⌘K listener is global while
 * mounted, so two would both fire. Render it inside `PageHeaderActions` to put
 * the trigger in the page header.
 */
export function PatientTableSearch<T>({
  rows,
  patient,
}: {
  rows: T[];
  /** How a row reads as a search result. Must be pure — it runs per keystroke. */
  patient: (item: T) => PatientResult;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Read through a ref (as `useShortcut` does) so re-folding tracks the data
  // rather than the identity of the inline lambda every call site passes.
  const patientRef = useRef(patient);
  patientRef.current = patient;

  // Fold every row once per data change rather than once per keystroke. Only
  // the matched-against text is cached; the rendered result is built fresh for
  // the handful of rows actually shown, so it can't go stale on a locale change.
  const folded = useMemo(
    () =>
      rows.map((item) => {
        const { id, name } = patientRef.current(item);
        // Id first: a numeric token prefix-matches, so "12" finds patient 1234.
        // The name is folded so "Sperka" finds "Šperka".
        return { item, haystacks: [String(id), foldText(name ?? "")] };
      }),
    [rows],
  );

  const tokens = useMemo(() => searchTokens(query), [query]);
  const matches = useMemo(
    () =>
      tokens.length === 0
        ? folded
        : folded.filter((row) => matchesTokens(row.haystacks, tokens)),
    [folded, tokens],
  );

  function close() {
    setOpen(false);
    setQuery("");
  }

  function handleSelect(id: number) {
    close();
    void navigate(`/patient/${id}`);
  }

  return (
    <SearchPalette
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
      query={query}
      onQueryChange={setQuery}
      triggerLabel={t`Search patients...`}
      inputPlaceholder={t`Search by name or ID...`}
      title={t`Patient Search`}
      description={t`Search patients by name or ID`}
      emptyMessage={t`No patients found.`}
    >
      <PatientResults
        results={matches
          .slice(0, MAX_SEARCH_RESULTS)
          .map(({ item }) => patient(item))}
        matchCount={matches.length}
        onSelect={handleSelect}
      />
    </SearchPalette>
  );
}

/**
 * The result list both patient palettes render, so a patient reads the same
 * whichever one found them. Callers cap `results` at {@link MAX_SEARCH_RESULTS}
 * and pass the full `matchCount`, which the heading reports when it's larger.
 */
export function PatientResults({
  results,
  matchCount,
  onSelect,
}: {
  results: PatientResult[];
  matchCount: number;
  onSelect: (id: number) => void;
}) {
  const { t } = useLingui();
  if (results.length === 0) return null;

  return (
    <CommandGroup
      heading={
        matchCount > results.length
          ? t`First ${results.length} of ${matchCount} patients`
          : t`Patients`
      }
    >
      {results.map((patient) => (
        <SearchPaletteItem
          key={patient.id}
          value={`${patient.id} ${patient.name ?? ""}`}
          title={patient.name ?? t`Patient #${patient.id}`}
          badge={`#${patient.id}`}
          subtitle={patient.subtitle}
          onSelect={() => onSelect(patient.id)}
        />
      ))}
    </CommandGroup>
  );
}
